import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { GenderEnum } from '../../enums/genderEnum';
import ConfirmDialog from '../../components/ConfirmDialog';
import { convertGenderToString } from '../../utils/convertGender';
import SwitchComponent from '../../components/switch';
import { TEXT_TITLE_COLOR } from '../../constants/colors';
import { convertAdminTypeToString } from '../../utils/convertAdmin';
import { AdminEnumType } from '../../enums/adminEnum';
import { ProvinceModel } from '../../models/province';
import { District } from '../../models/district';
import useAdminAccountFormController from './controller/form';
import { MerChantModel } from '../../models/merChantModel';
import { RoleAndPermissionModel } from '../../models/roleAndPermission';
interface Props {
  onClose: VoidFunction;
  selectedAdmin: any;
  onFetchData: Function;
  setSelected: any;
  adminType: AdminEnumType
  adminId: string | undefined;
  merchantData: MerChantModel[]
  roleAndPermissionData: RoleAndPermissionModel[]
}

const FormComponent = ({ onClose, selectedAdmin, onFetchData, setSelected, adminType, adminId, merchantData, roleAndPermissionData }: Props) => {
  const { t } = useTranslation('global');
  const controller =
    useAdminAccountFormController({ selectedAdmin, onClose, onFetchData, setSelected, adminType, roleAndPermissionData });

  return (
    <Box>
      <IconButton onClick={() => { onClose(); controller.handleReset() }}>
        <ArrowBack />
      </IconButton>
      <Typography variant="h4" fontWeight='bold' color={TEXT_TITLE_COLOR}>{t(`${selectedAdmin ? 'admin_page.Update Admin' : 'admin_page.Create Admin'}`)}</Typography>
      <form onSubmit={(e) => controller.handleOpenConfirmFormAdmin(e)}>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 8,
                boxShadow:
                  '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                borderRadius: '10px'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <label htmlFor="image-input-admin">
                  <Avatar
                    src={controller.imageToShow}
                    alt=""
                    style={{ cursor: 'pointer', width: 150, height: 150, objectFit: 'contain' }}
                  />
                </label>
                <input
                  type="file"
                  id="image-input-admin"
                  onClick={(e: any) => {
                    e.target.value = '';
                  }}
                  onChange={controller.handleSelectedImage}
                  style={{ display: 'none' }}
                />
              </Box>
              <Box mt={2}>
                <Typography textAlign="center">Allowed *.jpeg, *.jpg, *.png</Typography>
                <Typography textAlign="center">Max size of 3.1 MB</Typography>
              </Box>
            </Box>
            {
              selectedAdmin && <Box
                sx={{ display: selectedAdmin?.id === adminId ? 'none' : 'flex', justifyContent: 'center', mt: 1, alignItems: 'center', gap: 2 }}
              >
                <Typography>
                  <span style={{ color: 'green' }}>{t("admin_page.Active")}</span> / <span style={{ color: 'red' }}>{t("admin_page.InActive")}</span>
                </Typography>
                <SwitchComponent value={controller.banUser} onChange={controller.handleBanUser} />
              </Box>
            }

          </Grid>
          <Grid item xs={12} sm={10} md={8}>
            <Box
              sx={{
                p: 4,
                boxShadow:
                  '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
                borderRadius: '10px'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Full Name")}</Typography>
                  <TextField
                    fullWidth
                    required
                    margin="normal"
                    inputRef={controller.fullName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Phone")}</Typography>
                  <TextField
                    fullWidth
                    required
                    margin="normal"
                    inputRef={controller.phone}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Email")}</Typography>
                  <TextField
                    fullWidth
                    required
                    margin='normal'
                    inputRef={controller.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Date of Birth")}</Typography>
                  <TextField
                    fullWidth
                    required
                    margin='normal'
                    type='date'
                    inputRef={controller.dateOfBirth}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Province")}</Typography>
                  <FormControl fullWidth margin='normal'>
                    <Select value={controller.provinceValue} onChange={controller.handleSelectedProvince}>
                      {
                        controller.provinceData?.map((data: ProvinceModel) => (
                          <MenuItem key={data?.id} value={data?.provinceNameLA}>
                            {data?.provinceNameLA}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.City")}</Typography>
                  <FormControl fullWidth margin='normal'>
                    <Select value={controller.districtsValue} onChange={controller.handleSelectedDistrict}>
                      {
                        controller.districtsData?.map((dis: District) => (
                          <MenuItem key={dis?.districtCode} value={dis?.districtNameLA}>
                            {dis?.districtNameLA}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Village")}</Typography>
                  <TextField
                    fullWidth
                    required
                    margin='normal'
                    inputRef={controller.village}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.Gender")}</Typography>
                  <FormControl fullWidth margin='normal'>
                    <Select value={controller.selectedGender} onChange={controller.handleSelectedGender}>
                      {
                        Object.values(GenderEnum).map((gender: GenderEnum) => (
                          <MenuItem key={gender} value={gender}>
                            {convertGenderToString(gender)}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t("admin_page.User Name")}</Typography>
                  <TextField
                    fullWidth
                    required
                    margin='normal'
                    inputRef={controller?.userName}
                    disabled={selectedAdmin}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>{t('admin_page.Role')}</Typography>
                  <FormControl fullWidth margin="normal">
                    <Select value={controller.roleAndPermissionValue} onChange={(e) => { controller.handleChangeRoleAndPermissionValue(e.target.value) }}>
                      {roleAndPermissionData.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {convertAdminTypeToString(option.role_type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {
                  selectedAdmin ? <Box></Box> : <>
                    <Grid item xs={12} sm={6}>
                      <Typography>{t("admin_page.Password")}</Typography>
                      <TextField
                        type={controller.showPassword ? 'text' : 'password'}
                        fullWidth
                        required
                        margin='normal'
                        inputRef={controller.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                edge="end"
                                onClick={controller.handleShowPassword}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={selectedAdmin}
                              >
                                {controller.showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        disabled={selectedAdmin}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography>{t("admin_page.Confirm Password")}</Typography>
                      <TextField
                        type={controller.showConfirmPassword ? 'text' : 'password'}
                        fullWidth
                        required
                        margin='normal'
                        inputRef={controller.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                edge="end"
                                onClick={controller.handleShowConfirmPassword}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={selectedAdmin}
                              >
                                {controller.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        disabled={selectedAdmin}
                      />
                    </Grid>
                  </>
                }
                <Grid item xs={12} sm={6} sx={{ display: controller.roleData[0]?.role_type === "ADMIN" ? 'block' : 'none' }}>
                  <Typography>{t("Branch")}</Typography>
                  <FormControl fullWidth margin='normal'>
                    <Select
                      labelId="demo-multi-select-label"
                      id="demo-multi-select"
                      multiple
                      value={controller.merchantValue}
                      onChange={controller.handleSelectMerchant}
                    >
                      {merchantData?.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store?.nameLa}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {controller.errorMessage && <Typography color='error'>{controller.errorMessage}</Typography>}
              <Box mt={3} sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color='secondary'
                  sx={{ textTransform: 'none', width: 100, color: '#fff' }}
                >
                  {t(`${selectedAdmin ? 'Edit' : 'Create'}`)}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <ConfirmDialog
          open={controller.openConfirm}
          title={t(`${selectedAdmin ? 'admin_page.Do you want to update this data ?' : 'admin_page.Do you want to create this data ?'}`)}
          loading={controller.loadingConfirmForm}
          onCancel={controller.handleCloseConfirmForm}
          onConfirm={() => controller.handleSubmit()}
        />
      </form>
    </Box>
  );
};
export default FormComponent;
